/* ═══════════════════════════════════════════════════════════════════════════
   RNG seedé (mulberry32).

   Math.random() est proscrit dans la simulation : non seedable, non
   reproductible, et il rendrait impossibles les replays, l'attract mode, les
   golden tests et la re-simulation serveur.

   DEUX instances vivent dans le monde, et c'est délibéré (PLAN.md §3.2) :
     - `gameplay` : tout ce qui influence l'issue d'une partie (dispersion d'un
       tir, drops, choix de pattern). Il est seedé, sérialisé, rejoué.
     - `visual`   : ce qui n'a aucun effet mécanique (angle d'un débris, teinte
       d'une explosion, variation d'un son).

   Les séparer, c'est ce qui permet d'ajouter ou retirer un effet visuel sans
   décaler la suite du flux aléatoire du gameplay et invalider tous les replays
   enregistrés. Sans cette séparation, changer une gerbe d'étincelles casse un
   leaderboard.
   ═══════════════════════════════════════════════════════════════════════════ */

export class Rng {
  private s: number;

  constructor(seed: number) {
    // |0 force un entier 32 bits signé : le même point de départ partout.
    this.s = seed | 0;
  }

  /** État interne — sérialisable, pour snapshot et reprise de replay. */
  get state(): number {
    return this.s;
  }
  set state(v: number) {
    this.s = v | 0;
  }

  /** Flottant dans [0, 1). */
  next(): number {
    this.s = (this.s + 0x6d2b79f5) | 0;
    let t = this.s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Entier dans [0, n). */
  int(n: number): number {
    return Math.floor(this.next() * n);
  }

  /** Flottant dans [lo, hi). */
  range(lo: number, hi: number): number {
    return lo + this.next() * (hi - lo);
  }

  /** Flottant dans [-a, +a]. Le jitter le plus courant sur un pattern. */
  spread(a: number): number {
    return (this.next() * 2 - 1) * a;
  }

  clone(): Rng {
    return new Rng(this.s);
  }
}

/* Math.imul est une multiplication entière 32 bits exacte, définie au bit près
   par la spec — contrairement aux transcendantes. Elle est donc parfaitement
   déterministe, et c'est pour ça que mulberry32 convient ici. */
